import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import SearchBar from '../components/SearchBar';
import Layout from '../components/Layout';

import CustomerCreateModal from '../components/Customer/CustomerCreateModal';
import CreateQueryModal from '../components/Query/CreateQueryModal';
import CreateFollowUpModal from '../components/Followup/CreateFollowupModal';
import CustomerInfoModal from '../components/Customer/CustomerInfoModal';

import { Customer } from '../types/Customer';
import '../css/Pages.css';
import '../css/PackagesPage.css';
import { fetchCustomers } from '../api/Customer';
import * as FiIcons from 'react-icons/fi';
const FaEye = FiIcons.FiEye as unknown as React.FC;
const FaPlus = FiIcons.FiPlus as unknown as React.FC;
const FiRefreshCw = FiIcons.FiRefreshCw as unknown as React.FC;
const FiClock = FiIcons.FiClock as unknown as React.FC;

const CustomersPage = () => {
  const hasFetched = useRef(false);
  const [customers, setCustomers] = useState<Customer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomerCreateModal, setShowCustomerCreateModal] = useState(false);
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [showCreateFollowUpModal, setShowCreateFollowUpModal] = useState(false);
  const [customerId, setCustomerId] = useState(0);
  const [queryId, setQueryId] = useState(0);
  const [loading, setLoading] = useState(false);
  // Track which single field is revealed at a time: `${field}:${idOrIndex}`
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const maskPhone = (phone?: string) => {
    const s = String(phone || '').replace(/\s+/g, '');
    if (!s) return '-';
    const keep = 2;
    const endKeep = 2;
    const len = s.length;
    if (len <= keep + endKeep) return '*'.repeat(Math.max(3, len));
    const maskedLen = Math.max(3, len - keep - endKeep);
    return `${s.slice(0, keep)}${'*'.repeat(maskedLen)}${s.slice(-endKeep)}`;
  };

  const maskEmail = (email?: string) => {
    const s = String(email || '').trim();
    if (!s) return '-';
    const keep = 2;
    const endKeep = 2;
    const len = s.length;
    if (len <= keep + endKeep) return '*'.repeat(Math.max(3, len));
    const maskedLen = Math.max(3, len - keep - endKeep);
    return `${s.slice(0, keep)}${'*'.repeat(maskedLen)}${s.slice(-endKeep)}`;
  };

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchCustomers();
      setCustomers(data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    load();
  }, []);

  const closeAllModals = () => {
    setShowCustomerCreateModal(false);
    setShowCreateQueryModal(false);
    setShowCreateFollowUpModal(false);
  };

  const handleAddQuery = (customerId?: number) => {
    setCustomerId(customerId || 0);
    setShowCreateQueryModal(true);
  };

  const navigate = useNavigate();
  const handleViewQueries = (customerId?: number) => {
    navigate(`/queries?customerId=${customerId}`);
  };

  const filteredCustomers = customers.filter(c =>
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.phoneNumber || '').toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.city || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <div className="packages-header">
        <h2>Customers</h2>
        <div className="packages-actions">
          <button className="btn" onClick={load} disabled={loading} title="Reload customers">
            <FiRefreshCw /> {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button className="btn" onClick={() => setShowCustomerCreateModal(true)} title="Create a new customer">
            <FaPlus /> Add Customer
          </button>
        </div>
      </div>


      <div className="packages-filters">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search by name, phone, or city"
        />
      </div>

      <div className="packages-table-wrapper">
        <table className="packages-table customers-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>City</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers
              .map((c, idx) => (
                <tr key={c.id ?? idx}>
                  <td>
                    <span
                      className="clickable-name"
                      onClick={() => setSelectedCustomer(c)}
                    >
                      {c.name}
                    </span>
                  </td>
                  <td>
                    {(() => {
                      const key = `email:${c.id ?? 'idx-'+idx}`;
                      const isShown = revealedKey === key;
                      return isShown ? (
                        <a href={`mailto:${c.email}`}>{c.email}</a>
                      ) : (
                        <span className="masked" onClick={() => setRevealedKey(key)} title="Click to reveal email">
                          {maskEmail(c.email)}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {(() => {
                      const key = `phone:${c.id ?? 'idx-'+idx}`;
                      const isShown = revealedKey === key;
                      return isShown ? (
                        <a href={`tel:${c.phoneNumber}`}>{c.phoneNumber}</a>
                      ) : (
                        <span className="masked" onClick={() => setRevealedKey(key)} title="Click to reveal phone">
                          {maskPhone(c.phoneNumber)}
                        </span>
                      );
                    })()}
                  </td>
                  <td>{c.city || '-'}</td>
                  <td className="col-actions">
                    <div className="action-buttons with-labels">
                      <button className="btn-secondary" title="View Queries" onClick={() => handleViewQueries(c.id)}>
                        <FaEye /> <span className="btn-label">View Queries</span>
                      </button>
                      <button className="btn-secondary" title="Add Query" onClick={() => handleAddQuery(c.id)}>
                        <FaPlus /> <span className="btn-label">Add Query</span>
                      </button>
                      <button className="btn-secondary" title="View Follow-ups" onClick={() => navigate(`/followups?customerId=${c.id}`)}>
                        <FiClock /> <span className="btn-label">Follow-ups</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            {filteredCustomers.length === 0 && (
              <tr>
                <td colSpan={5} className="empty-state">No customers found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {showCustomerCreateModal && (
        <CustomerCreateModal
          onClose={closeAllModals}
          onCustomerCreated={async (id) => {
            setCustomerId(id);
            setShowCustomerCreateModal(false);
            await load();
            setShowCreateQueryModal(true);
          }}
        />
      )}

      {showCreateQueryModal && (
        <CreateQueryModal
          customerId={customerId}
          onClose={closeAllModals}
          onQueryCreated={(id) => {
            setQueryId(id);
            setShowCreateQueryModal(false);
            setShowCreateFollowUpModal(true);
          }}
        />
      )}

      {showCreateFollowUpModal && (
        <CreateFollowUpModal
          queryId={queryId}
          onClose={closeAllModals}
        />
      )}
    </Layout>
  );
};

export default CustomersPage;
